import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AuthUser } from 'aws-amplify/auth';
import { Match } from '../types';
import { getMatches } from '../api';
import './Home.css';

interface Props {
  user: AuthUser | null;
}

// Authoritative team name → flag code + nationality adjective.
// Derived from the validated p.team, so AI hallucinations about nationalityCode don't matter.
const TEAM_META: Record<string, { code: string; nationality: string }> = {
  // South America
  Argentina: { code: 'AR', nationality: 'Argentine' },
  Brazil: { code: 'BR', nationality: 'Brazilian' },
  Uruguay: { code: 'UY', nationality: 'Uruguayan' },
  Colombia: { code: 'CO', nationality: 'Colombian' },
  Ecuador: { code: 'EC', nationality: 'Ecuadorian' },
  Chile: { code: 'CL', nationality: 'Chilean' },
  Paraguay: { code: 'PY', nationality: 'Paraguayan' },
  Bolivia: { code: 'BO', nationality: 'Bolivian' },
  Venezuela: { code: 'VE', nationality: 'Venezuelan' },
  Peru: { code: 'PE', nationality: 'Peruvian' },
  // North/Central America & Caribbean
  'United States': { code: 'US', nationality: 'American' },
  USA: { code: 'US', nationality: 'American' },
  Mexico: { code: 'MX', nationality: 'Mexican' },
  Canada: { code: 'CA', nationality: 'Canadian' },
  Honduras: { code: 'HN', nationality: 'Honduran' },
  'Costa Rica': { code: 'CR', nationality: 'Costa Rican' },
  Panama: { code: 'PA', nationality: 'Panamanian' },
  'El Salvador': { code: 'SV', nationality: 'Salvadoran' },
  Jamaica: { code: 'JM', nationality: 'Jamaican' },
  Haiti: { code: 'HT', nationality: 'Haitian' },
  Cuba: { code: 'CU', nationality: 'Cuban' },
  Guatemala: { code: 'GT', nationality: 'Guatemalan' },
  Suriname: { code: 'SR', nationality: 'Surinamese' },
  Curacao: { code: 'CW', nationality: 'Curaçaoan' },
  'Trinidad and Tobago': { code: 'TT', nationality: 'Trinidadian' },
  // Europe
  France: { code: 'FR', nationality: 'French' },
  Spain: { code: 'ES', nationality: 'Spanish' },
  Germany: { code: 'DE', nationality: 'German' },
  Portugal: { code: 'PT', nationality: 'Portuguese' },
  England: { code: 'ENG', nationality: 'English' },
  Netherlands: { code: 'NL', nationality: 'Dutch' },
  Belgium: { code: 'BE', nationality: 'Belgian' },
  Italy: { code: 'IT', nationality: 'Italian' },
  Croatia: { code: 'HR', nationality: 'Croatian' },
  Switzerland: { code: 'CH', nationality: 'Swiss' },
  Serbia: { code: 'RS', nationality: 'Serbian' },
  Austria: { code: 'AT', nationality: 'Austrian' },
  Turkey: { code: 'TR', nationality: 'Turkish' },
  Türkiye: { code: 'TR', nationality: 'Turkish' },
  Scotland: { code: 'SCT', nationality: 'Scottish' },
  Wales: { code: 'WLS', nationality: 'Welsh' },
  Denmark: { code: 'DK', nationality: 'Danish' },
  Sweden: { code: 'SE', nationality: 'Swedish' },
  Norway: { code: 'NO', nationality: 'Norwegian' },
  Poland: { code: 'PL', nationality: 'Polish' },
  Ukraine: { code: 'UA', nationality: 'Ukrainian' },
  Romania: { code: 'RO', nationality: 'Romanian' },
  'Czech Republic': { code: 'CZ', nationality: 'Czech' },
  Czechia: { code: 'CZ', nationality: 'Czech' },
  Slovakia: { code: 'SK', nationality: 'Slovak' },
  Hungary: { code: 'HU', nationality: 'Hungarian' },
  Slovenia: { code: 'SI', nationality: 'Slovenian' },
  Albania: { code: 'AL', nationality: 'Albanian' },
  Greece: { code: 'GR', nationality: 'Greek' },
  Iceland: { code: 'IS', nationality: 'Icelandic' },
  Georgia: { code: 'GE', nationality: 'Georgian' },
  // Africa
  Morocco: { code: 'MA', nationality: 'Moroccan' },
  Senegal: { code: 'SN', nationality: 'Senegalese' },
  Nigeria: { code: 'NG', nationality: 'Nigerian' },
  Egypt: { code: 'EG', nationality: 'Egyptian' },
  Algeria: { code: 'DZ', nationality: 'Algerian' },
  Tunisia: { code: 'TN', nationality: 'Tunisian' },
  Cameroon: { code: 'CM', nationality: 'Cameroonian' },
  Ghana: { code: 'GH', nationality: 'Ghanaian' },
  'Ivory Coast': { code: 'CI', nationality: 'Ivorian' },
  "Côte d'Ivoire": { code: 'CI', nationality: 'Ivorian' },
  'South Africa': { code: 'ZA', nationality: 'South African' },
  'DR Congo': { code: 'CD', nationality: 'Congolese' },
  Tanzania: { code: 'TZ', nationality: 'Tanzanian' },
  Mali: { code: 'ML', nationality: 'Malian' },
  Kenya: { code: 'KE', nationality: 'Kenyan' },
  Angola: { code: 'AO', nationality: 'Angolan' },
  Zambia: { code: 'ZM', nationality: 'Zambian' },
  Zimbabwe: { code: 'ZW', nationality: 'Zimbabwean' },
  // Asia
  Japan: { code: 'JP', nationality: 'Japanese' },
  'South Korea': { code: 'KR', nationality: 'South Korean' },
  'Korea Republic': { code: 'KR', nationality: 'South Korean' },
  Australia: { code: 'AU', nationality: 'Australian' },
  'Saudi Arabia': { code: 'SA', nationality: 'Saudi' },
  Iran: { code: 'IR', nationality: 'Iranian' },
  Iraq: { code: 'IQ', nationality: 'Iraqi' },
  Uzbekistan: { code: 'UZ', nationality: 'Uzbek' },
  Indonesia: { code: 'ID', nationality: 'Indonesian' },
  China: { code: 'CN', nationality: 'Chinese' },
  'China PR': { code: 'CN', nationality: 'Chinese' },
  'New Zealand': { code: 'NZ', nationality: 'New Zealander' },
  Jordan: { code: 'JO', nationality: 'Jordanian' },
  Bahrain: { code: 'BH', nationality: 'Bahraini' },
  Qatar: { code: 'QA', nationality: 'Qatari' },
  // Oceania
  'New Caledonia': { code: 'NC', nationality: 'New Caledonian' },
  Fiji: { code: 'FJ', nationality: 'Fijian' },
  Tahiti: { code: 'PF', nationality: 'Tahitian' },
};

// Regional flags for UK nations use Unicode tag sequences, not the standard A-Z indicator pairs
const SPECIAL_FLAGS: Record<string, string> = {
  ENG: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
  SCT: '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
  WLS: '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
};

function toFlag(code: string): string {
  const c = code.toUpperCase().trim();
  if (SPECIAL_FLAGS[c]) return SPECIAL_FLAGS[c];
  if (!/^[A-Z]{2}$/.test(c)) return '';
  return String.fromCodePoint(...c.split('').map(ch => 0x1F1E6 + ch.charCodeAt(0) - 65));
}

interface PlayerCardProps {
  match: Match;
}

function PlayerCard({ match }: PlayerCardProps) {
  const p = match.aiPrediction.starPlayer!;
  const [imgSrc, setImgSrc] = useState(p.photoUrl ?? '');
  const [imgFailed, setImgFailed] = useState(false);
  // TheSportsDB's strCountry is the authoritative source for a player's nationality —
  // it overrides whatever team the backend AI hallucinated (e.g. Mbappe→Scotland)
  const [tsdbCountry, setTsdbCountry] = useState('');

  useEffect(() => {
    if (!p.name) return;
    let cancelled = false;
    fetch(`https://www.thesportsdb.com/api/v1/json/3/searchplayers.php?p=${encodeURIComponent(p.name)}`)
      .then(r => r.json() as Promise<{ player?: { strCutout?: string; strThumb?: string; strCountry?: string; strNationality?: string }[] }>)
      .then(d => {
        if (cancelled) return;
        const first = d?.player?.[0];
        const url = first?.strCutout || first?.strThumb || '';
        if (url) setImgSrc(url);
        // strCountry e.g. "France" — use to look up TEAM_META and get correct flag
        if (first?.strCountry) setTsdbCountry(first.strCountry);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [p.name]);

  // Prefer TheSportsDB country (authoritative), fall back to backend p.team
  const displayCountry = tsdbCountry || p.team || '';
  const teamMeta = TEAM_META[displayCountry] || TEAM_META[p.team ?? ''];
  const flag = teamMeta ? toFlag(teamMeta.code) : '';
  const nationality = teamMeta?.nationality || displayCountry;

  const kickoff = new Date(match.kickoffTime);
  const timeStr = kickoff.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const today = new Date().toDateString() === kickoff.toDateString();
  const tomorrow = new Date(Date.now() + 86400000).toDateString() === kickoff.toDateString();
  const dayLabel = today ? 'Today' : tomorrow ? 'Tomorrow' : kickoff.toLocaleDateString([], { month: 'short', day: 'numeric' });
  const initials = p.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const showPhoto = !!imgSrc && !imgFailed;

  return (
    <Link to="/matches" className="player-card">
      <div className="player-card-photo-wrap">
        {showPhoto ? (
          <img
            src={imgSrc}
            alt={p.name}
            className="player-card-photo"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="player-card-avatar">{initials}</div>
        )}
        {flag && <div className="player-card-flag-badge">{flag}</div>}
      </div>
      <div className="player-card-body">
        <div className="player-card-nation">{flag && `${flag} `}{nationality}</div>
        <div className="player-card-name">{p.name}</div>
        <div className="player-card-team">{p.team}</div>
        <p className="player-card-wc">{p.wc2026Context || p.insight}</p>
        <div className="player-card-match">
          <span className="player-card-match-teams">{match.homeTeam} vs {match.awayTeam}</span>
          <span className="player-card-match-time">{dayLabel} · {timeStr}</span>
        </div>
        <div className="player-card-cta">Predict this match →</div>
      </div>
    </Link>
  );
}

function useCountUp(target: number, visible: boolean, duration = 1200): number {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!visible || target === 0) return;
    setValue(0);
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, visible, duration]);

  return value;
}

const HERO_PHRASES = [
  { plain: 'AI Picks First.', accent: 'You Pick Better.' },
  { plain: 'Challenge', accent: 'the AI.' },
  { plain: 'Earn More', accent: 'for Being Bold.' },
];

const TAGLINE_PHRASES = [
  { accent: 'Challenge the AI', rest: ' — earn more for being right.' },
  { accent: 'Think independently', rest: ' — blind picks earn the most.' },
  { accent: 'AI reasons out loud', rest: ' — then you decide.' },
  { accent: 'Beat the AI', rest: ' — +12 points for proving it wrong.' },
  { accent: 'Climb the board', rest: ' — top players challenge, not follow.' },
];

export default function Home({ user }: Props) {
  const username = user?.signInDetails?.loginId?.split('@')[0] ?? null;
  const [fixtureCount, setFixtureCount] = useState(0);
  const [watchMatches, setWatchMatches] = useState<Match[]>([]);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLElement>(null);

  useEffect(() => {
    getMatches().then(data => {
      const upcoming = data.filter(m => m.status !== 'finished');
      setFixtureCount(upcoming.length);
      // Only matches that have a starPlayer with the new wc2026 fields
      setWatchMatches(upcoming.filter(m => m.aiPrediction.starPlayer?.name));
    });
  }, []);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStatsVisible(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const animatedFixtures = useCountUp(fixtureCount, statsVisible);
  const animatedAccuracy = useCountUp(74, statsVisible);
  const animatedLeaderboard = useCountUp(15, statsVisible);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.play()
      .then(() => setVideoReady(true))
      .catch(() => setVideoReady(false));
  }, []);

  // Hero state machine: words → cards → words → cards → ...
  const [heroPhase, setHeroPhase] = useState<'words' | 'cards'>('words');
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroPrevIdx, setHeroPrevIdx] = useState<number | null>(null);

  useEffect(() => {
    if (heroPhase === 'words') {
      const t = setTimeout(() => setHeroPhase('cards'), 2800);
      return () => clearTimeout(t);
    }

    const t = setTimeout(() => {
      const isLast = heroIdx === HERO_PHRASES.length - 1;
      setHeroPrevIdx(heroIdx);

      if (isLast) {
        setTimeout(() => {
          setHeroPrevIdx(null);
          setHeroIdx(0);
          setHeroPhase('words');
        }, 500);
      } else {
        setTimeout(() => setHeroPrevIdx(null), 500);
        setHeroIdx(heroIdx + 1);
      }
    }, 3000);
    return () => clearTimeout(t);
  }, [heroPhase, heroIdx]);

  // Tagline cycling
  const [taglineIdx, setTaglineIdx] = useState(0);
  const [taglineVisible, setTaglineVisible] = useState(true);

  useEffect(() => {
    const id = setInterval(() => {
      setTaglineVisible(false);
      setTimeout(() => {
        setTaglineIdx(i => (i + 1) % TAGLINE_PHRASES.length);
        setTaglineVisible(true);
      }, 380);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="home-page container">
      <section className="hero">
        <video
          ref={videoRef}
          className="hero-video"
          muted
          loop
          playsInline
          aria-hidden="true"
          style={{ display: videoReady ? 'block' : 'none' }}
        >
          <source src="/videos/banner.mp4" type="video/mp4" />
        </video>
        <div className="hero-overlay" aria-hidden="true" />
        <div className="hero-content">
          <div className="hero-badge">
            {username ? <>⚡ Welcome back, <strong>{username}</strong></> : '⚡ AI-POWERED FOOTBALL PREDICTIONS'}
          </div>

          <div className="hero-title-wrap">
            {heroPhase === 'words' ? (
              <h1 className="hero-title hero-words-block">
                <span className="hero-word hero-word--1">AI Picks</span>{' '}
                <span className="hero-word hero-word--2">First.</span>
                <br />
                <span className="hero-word hero-word--3 hero-accent-gradient">You Pick Better.</span>
              </h1>
            ) : (
              <>
                {heroPrevIdx !== null && (
                  <h1 key={`exit-${heroPrevIdx}`} className="hero-title hero-title--exit">
                    {HERO_PHRASES[heroPrevIdx].plain}<br />
                    <span className="hero-accent-gradient">{HERO_PHRASES[heroPrevIdx].accent}</span>
                  </h1>
                )}
                <h1 key={`enter-${heroIdx}`} className="hero-title hero-title--enter">
                  {HERO_PHRASES[heroIdx].plain}<br />
                  <span className="hero-accent-gradient">{HERO_PHRASES[heroIdx].accent}</span>
                </h1>
              </>
            )}
          </div>

          <p className="hero-sub">
            See exactly why our AI predicts each winner — then earn bonus points for proving it wrong.
          </p>

          <div className="hero-actions">
            <Link to="/matches" className="btn-primary">Start Predicting Free →</Link>
            <Link to="/matches" className="btn-secondary">See Today's AI Picks</Link>
          </div>
        </div>
      </section>

      {watchMatches.length > 0 && (() => {
        const allToday = watchMatches.every(m => new Date().toDateString() === new Date(m.kickoffTime).toDateString());
        const sectionLabel = allToday ? 'Playing Today' : 'Playing This Week';
        return (
        <section className="players-watch">
          <div className="players-watch-header">
            <div>
              <h2 className="players-watch-title">⭐ World Cup Stars — {sectionLabel}</h2>
              <p className="players-watch-sub">FIFA World Cup 2026 · The elite players in action today</p>
            </div>
            <Link to="/matches" className="players-watch-all">See all matches →</Link>
          </div>
          <div className="players-watch-strip">
            {watchMatches.map(match => (
              <PlayerCard key={match.matchId} match={match} />
            ))}
          </div>
        </section>
        );
      })()}

      <section className="stats-row" ref={statsRef}>
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-val">{animatedFixtures}</div>
          <div className="stat-label">Matches Today</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🤖</div>
          <div className="stat-val">{animatedAccuracy}%</div>
          <div className="stat-label">AI Win Rate</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🏆</div>
          <div className="stat-val">{animatedLeaderboard}</div>
          <div className="stat-label">Active Players</div>
        </div>
        <div className="stat-card stat-card--streak">
          <div className="stat-icon">🔥</div>
          <div className="stat-val">{user ? '—' : '?'}</div>
          <div className="stat-label">Your Streak 🔥</div>
        </div>
        <Link to="/leaderboard" className="stat-card stat-card--leaderboard-cta">
          <div className="stat-icon">🏅</div>
          <div className="stat-val stat-val--cta">View</div>
          <div className="stat-label">Leaderboard</div>
        </Link>
      </section>

      <section className="how-it-works">
        <h2 className="section-title">How TopQik Works</h2>
        <div className="steps">
          <div className="step">
            <div className="step-num">01</div>
            <h3>AI Analyzes</h3>
            <p>Amazon Nova AI studies form, head-to-head history, player form and team stats — then makes its prediction with full natural language reasoning.</p>
          </div>
          <div className="step">
            <div className="step-num">02</div>
            <h3>You Decide</h3>
            <p>Read the AI insights — then think for yourself. Agree with AI or challenge it. Independent thinkers earn more points.</p>
          </div>
          <div className="step">
            <div className="step-num">03</div>
            <h3>Earn &amp; Climb</h3>
            <p>Beat the AI blind = 15pts. Challenge and win = 12pts. Blindly follow and win = just 3pts. Think. Don't follow.</p>
          </div>
        </div>
        <p className={`how-tagline${taglineVisible ? ' how-tagline--visible' : ''}`}>
          <span className="hero-accent">{TAGLINE_PHRASES[taglineIdx].accent}</span>
          {TAGLINE_PHRASES[taglineIdx].rest}
        </p>
      </section>
    </div>
  );
}
